import { useState } from "react";
import { api, unwrap } from "../api/client";
import type { InputView, StateMessage } from "../api/types";
import { InputForm } from "../components/InputForm";
import { InputHealthPill } from "../components/status";
import { Button, ConfirmDialog, CopyField, EmptyState, PageHeader, Tag, useToast } from "../components/ui";
import { useCan, useClientLimits } from "../lib/auth";
import { formatBitrate, PROTOCOL_LABEL } from "../lib/format";
import { t } from "../lib/i18n";
import { href, navigate } from "../lib/router";

export function Inputs({ state }: { state: StateMessage }) {
  const toast = useToast();
  const can = useCan();
  const isClient = useClientLimits() !== null;
  const canEdit = can("operator");   // créer et modifier ; supprimer reste aux administrateurs
  const [editing, setEditing] = useState<InputView | "new" | null>(null);
  const [deleting, setDeleting] = useState<InputView | null>(null);
  const [busy, setBusy] = useState(false);

  const usedBy = (id: string) => state.outputs.filter((o) => o.input_id === id);

  const remove = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await unwrap(api.DELETE("/api/inputs/{input_id}", { params: { path: { input_id: deleting.id } } }));
      toast(t("Entrée « {name} » supprimée", { name: deleting.name }));
      setDeleting(null);
    } catch (e) {
      toast((e as Error).message, "err");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title={t("Entrées")} subtitle={t("Flux reçus par la gateway")}
        actions={canEdit && <Button variant="primary" onClick={() => setEditing("new")}>{t("Nouvelle entrée")}</Button>} />

      {state.inputs.length === 0 ? (
        <EmptyState title={t("Aucune entrée")} action={canEdit && <Button variant="primary" onClick={() => setEditing("new")}>{t("Créer une entrée")}</Button>} />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-line bg-surface">
          <table className="w-full min-w-[820px] text-left">
            <thead className="border-b border-line font-cond text-[12px] uppercase tracking-wider text-faint">
              <tr>
                <th className="px-4 py-2 font-semibold">{t("État")}</th>
                <th className="px-4 py-2 font-semibold">{t("Entrée")}</th>
                <th className="px-4 py-2 font-semibold">{t("Type")}</th>
                <th className="px-4 py-2 font-semibold">{t("URL encodeur / source")}</th>
                <th className="px-4 py-2 text-right font-semibold">{t("Débit")}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {state.inputs.map((i) => (
                <tr key={i.id} className="align-middle">
                  <td className="px-4 py-2.5"><InputHealthPill input={i} alarms={state.alarms} /></td>
                  <td className="px-4 py-2.5">
                    <a href={href({ page: "input", id: i.id })} className="font-medium hover:text-accent">{i.name}</a>
                    <div className="font-mono text-[12px] text-faint">
                      {i.id}{i.owner && !isClient && <span className="font-sans"> · {t("client {name}", { name: i.owner })}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-2.5"><Tag>{PROTOCOL_LABEL[i.protocol]} · {i.mode}</Tag></td>
                  <td className="max-w-[380px] px-4 py-2.5">
                    {i.push_url ? <CopyField value={i.push_url} />
                      : i.mode === "push" ? <span className="text-[12px] text-faint">{t("Visible par les opérateurs")}</span>
                      : <span className="block truncate font-mono text-[12px] text-muted" title={i.source_url ?? ""}>{i.source_url}</span>}
                  </td>
                  <td className="num px-4 py-2.5 text-right">{formatBitrate(i.live.bitrate_in_kbps)}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => navigate({ page: "input", id: i.id })}>{t("Détail")}</Button>
                      {canEdit && <Button size="sm" variant="ghost" onClick={() => setEditing(i)}>{t("Modifier")}</Button>}
                      {can("admin") && <Button size="sm" variant="ghost" onClick={() => setDeleting(i)}>{t("Supprimer")}</Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <InputForm input={editing === "new" ? undefined : editing} onClose={() => setEditing(null)}
          onSaved={(saved) => {
            toast(editing === "new" ? t("Entrée « {name} » créée", { name: saved.name }) : t("Entrée « {name} » modifiée", { name: saved.name }));
            setEditing(null);
            if (editing === "new") navigate({ page: "input", id: saved.id });
          }} />
      )}

      <ConfirmDialog open={deleting !== null} title={t("Supprimer l'entrée ?")} confirmLabel={t("Supprimer")} busy={busy}
        onCancel={() => setDeleting(null)} onConfirm={remove}>
        {deleting && (usedBy(deleting.id).length > 0
          ? t("« {name} » alimente encore {outputs}. Supprimez ou réaffectez d'abord ces sorties.", { name: deleting.name, outputs: usedBy(deleting.id).map((o) => `« ${o.name} »`).join(", ") })
          : t("« {name} » sera supprimée et son encodeur déconnecté. Cette action est définitive.", { name: deleting.name }))}
      </ConfirmDialog>
    </div>
  );
}
